// M15 (share-loop) — prewritten boss/client framings for the share-mint
// success panel. Pure string assembly, zero deps: the same module backs
// the dialog's framed tabs and the ShareButtonsRow email button, and it
// unit-tests without a DOM.
//
// Copy is locked with marketing — change it here and the dialog, the
// mailto bodies, and the tests all move together. The `{pct}` stat
// variant only renders when the caller actually has the report's
// sourceMix at hand; the dialog currently doesn't receive the report
// payload, so it passes no stat and gets the no-stat body (see
// ShareOptInDialog props — we deliberately don't thread the multi-KB
// payload through just for one number).

export type ShareFraming = "boss" | "client";

export interface FramedMessageArgs {
  /** GA4 property display name — interpolated into subjects + the
   *  client body. */
  propertyName: string;
  /** The minted share URL (absolute). Always the last line of the body. */
  url: string;
  /** Social share of total site sessions as a 0..1 ratio
   *  (report.sourceMix.socialShare.sessions). Omit / null when the
   *  caller doesn't have the report payload — the boss body falls back
   *  to the no-stat variant. */
  socialShare?: number | null;
}

export interface FramedMessage {
  subject: string;
  body: string;
}

/** Assemble the prewritten subject + 2-line body for a framing. The body
 *  always ends with the share URL on its own line so "Copy message" and
 *  mailto consumers paste/encode identically. */
export function buildFramedMessage(framing: ShareFraming, args: FramedMessageArgs): FramedMessage {
  const { propertyName, url, socialShare } = args;

  if (framing === "boss") {
    const hasStat = typeof socialShare === "number" && Number.isFinite(socialShare);
    const body = hasStat
      ? `Quick read: social drove ${Math.round(socialShare * 100)}% of our site sessions this period. Full breakdown — sources, conversions, UTM health — in this live report:\n${url}`
      : `Quick read on what social actually drives on our site — sources, conversions, UTM health — in this live report:\n${url}`;
    return {
      subject: `Social's on-site impact — ${propertyName}`,
      body,
    };
  }

  // framing === "client"
  return {
    subject: `${propertyName} — social performance report`,
    body: `Here's the live social report for ${propertyName}: what each platform drove on-site, conversions, and a UTM health check. No login needed:\n${url}`,
  };
}

/** Clipboard form: subject line + blank line + body. */
export function framedMessageToClipboardText(message: FramedMessage): string {
  return `${message.subject}\n\n${message.body}`;
}

/** mailto: form with subject + body URL-encoded. encodeURIComponent
 *  escapes the newline as %0A, which mail clients render as a line
 *  break. */
export function framedMessageToMailtoHref(message: FramedMessage): string {
  return `mailto:?subject=${encodeURIComponent(message.subject)}&body=${encodeURIComponent(message.body)}`;
}
